// frontend/src/utils/costBreakdown.js
// ============================================================================
// Helper único para el desglose de costos de la cotización.
// Deriva la forma estructurada { viaticos, lugar, material, otro },
// cada uno { cobrado, gastado }, a partir de los datos CRUDOS de la cotización
// (Quote.items -> quote_details.items: service_details / logistics / costs).
//
// Se usa en la descarga (pdfGenerator), en la vista de Facturas Pendientes
// (InvoiceInbox) y en el modal de subida de factura (InvoiceUploadModal) para
// que todos muestren exactamente lo mismo.
// ============================================================================

// Convierte cualquier valor ("21,936.00", "16680", null, etc.) a número seguro.
const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

// Devuelve el primer valor "presente" (no null/undefined/''), o 0.
const pick = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && String(v).trim() !== '') return v;
  }
  return 0;
};

/**
 * Construye el desglose estructurado de costos.
 * @param {object} quoteItems - quote_details.items (Quote.items)
 * @returns {{viaticos:{cobrado:number,gastado:number}, lugar:{...}, material:{...}, otro:{...}}}
 */
export const buildCostBreakdown = (quoteItems) => {
  const items = quoteItems || {};
  const sd = items.service_details || {};
  const log = items.logistics || {};
  const c = items.costs || {};

  return {
    viaticos: {
      cobrado: num(log.viaticos_charged_amount),
      gastado: num(log.viaticos_cost),
    },
    lugar: {
      cobrado: num(log.lugar_charged_amount),
      gastado: num(log.lugar_cost),
    },
    material: {
      cobrado: num(pick(sd.material_charged, c.material_charged)),
      gastado: num(c.material_cost),
    },
    otro: {
      cobrado: num(c.other_charged),
      gastado: num(c.other_costs),
    },
  };
};

// True si el concepto tiene algún monto (cobrado o gastado) mayor a 0.
export const conceptHasAmount = (concept) =>
  !!concept && ((concept.cobrado || 0) > 0 || (concept.gastado || 0) > 0);

// True si algún concepto del desglose tiene monto.
export const hasAnyCost = (breakdown) =>
  Object.values(breakdown || {}).some(conceptHasAmount);

export default buildCostBreakdown;
